import {
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  CrawlerRecurrence,
  CrawlerRecurrenceValues,
  CrawlerStatus,
  CrawlerType,
} from 'src/app/models/integration/crawler';
import { Trademark } from 'src/app/models/trademark';
import { Option } from '../select/select.component';

export interface CrawlerFilterValues {
  status: CrawlerStatus[];
  recurrence: CrawlerRecurrence[];
  type: CrawlerType[];
  trademark: Trademark[];
  countries: string[];
  query: string;
}

/**
 * Filter bar + side drawer for the crawler list page (documents-page pattern).
 * Owns the reactive filters form; the page content (table + paginator) is
 * projected via <ng-content>. Status stays in the bar; the drawer holds
 * recurrence / type / trademark / countries.
 */
@Component({
  selector: 'app-crawler-filters-drawer',
  templateUrl: './crawler-filters-drawer.component.html',
  styleUrls: ['./crawler-filters-drawer.component.scss'],
})
export class CrawlerFiltersDrawerComponent {
  readonly count = input(0);
  readonly loading = input(false);
  readonly autoRefresh = input(false);

  readonly filtersChange = output<CrawlerFilterValues>();
  readonly refresh = output<void>();

  readonly filtersForm = new FormGroup({
    status: new FormControl<CrawlerStatus[]>([], { nonNullable: true }),
    recurrence: new FormControl<CrawlerRecurrence[]>([], { nonNullable: true }),
    type: new FormControl<CrawlerType[]>([], { nonNullable: true }),
    trademark: new FormControl<Trademark[]>([], { nonNullable: true }),
    countries: new FormControl<string[]>([], { nonNullable: true }),
    query: new FormControl<string>('', { nonNullable: true }),
  });

  readonly statuses: Option[] = Object.values(CrawlerStatus).map((value) => ({
    value,
    label: `INTEGRATIONS.STATUS_${value}`,
    selected: false,
    disabled: false,
  }));

  readonly recurrences: Option[] = [
    { value: CrawlerRecurrenceValues[2], label: 'DATES.ONCE', selected: false, disabled: false },
    { value: CrawlerRecurrenceValues[1], label: 'DATES.WEEKLY', selected: false, disabled: false },
    { value: CrawlerRecurrenceValues[0], label: 'DATES.MONTHLY', selected: false, disabled: false },
  ];

  readonly types: Option[] = [
    { value: CrawlerType.SINGLE_URL, label: 'INTEGRATIONS.TYPE_SINGLE_URL', selected: false, disabled: false },
    { value: CrawlerType.EXCEL_BATCH, label: 'INTEGRATIONS.TYPE_EXCEL_BATCH', selected: false, disabled: false },
    { value: CrawlerType.DISCOVERY, label: 'INTEGRATIONS.TYPE_DISCOVERY', selected: false, disabled: false },
  ];

  // Reactive form value as a signal — drives the active-filters badge instantly.
  private readonly formValue = toSignal(this.filtersForm.valueChanges, {
    initialValue: this.filtersForm.getRawValue(),
  });

  // Active filters living in the drawer (status is excluded — it stays in the bar).
  readonly activeFiltersCount = computed(() => {
    const f = this.formValue();
    return (f.recurrence?.length ? 1 : 0)
      + (f.type?.length ? 1 : 0)
      + (f.trademark?.length ? 1 : 0)
      + (f.countries?.length ? 1 : 0);
  });

  constructor() {
    // Apply filters on change (debounced); the refetch is driven by the parent page.
    this.filtersForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(),
      )
      .subscribe((value) => this.filtersChange.emit(value as CrawlerFilterValues));
  }
}
